/* ==========================================================================
   NoelGlow — Shipping Protection, in the cart
   --------------------------------------------------------------------------
   Vanilla JS, no dependencies. Loaded once per page from
   snippets/noelglow-addons-config.liquid (NOT from the card snippet: the
   cart drawer re-renders through innerHTML, and a <script> injected that way
   never executes).

   What this file does
   -------------------
   1. Listens for the protection checkbox in the CAPTURE phase on `document`,
      so one handler serves every card, in the drawer and on the cart page,
      and keeps working after the drawer replaces its own innerHTML. Nothing
      is ever bound per-card, so there is nothing to re-bind.

      The capture-phase listener also calls stopPropagation(). That is
      deliberate: `CartItems` in assets/cart.js has a debounced `change`
      listener on itself and would read `event.target.dataset.index` /
      `.value` off this checkbox and fire a bogus /cart/change.js. The card is
      already rendered outside <cart-items> / <cart-drawer-items> in both
      places, so this is belt-and-braces, not the only guard.

   2. Reconciles the real cart over Shopify's own endpoints:
        add     -> POST /cart/add.js      { items: [{ id, quantity: 1 }] }
        remove  -> POST /cart/change.js   { id: <LINE KEY>, quantity: 0 }
        cap     -> POST /cart/change.js   { id: <LINE KEY>, quantity: 1 }
      /cart/change.js is always called with the line's `key`, never with a
      variant id.

   3. Serialises every toggle through one promise chain, so a burst of rapid
      clicks can never produce two in-flight writes and therefore can never
      create a duplicate protection line. Each run re-reads /cart.js first,
      so the run is idempotent and the chain converges on whatever the
      shopper last asked for.

   4. Asks for the cart sections in the same request and re-renders them the
      way the theme does, so the line items, the cart count and the subtotal
      update without a page reload.

   5. On failure, re-reads the cart and puts the checkbox back to the truth,
      then shows a short message in an aria-live region.

   6. Reads the cart once on boot and syncs every card to it, which covers a
      cart changed in another tab or restored from the back/forward cache.

   What this file deliberately does NOT do
   ---------------------------------------
   - It never adds protection on its own: only a shopper's click does.
   - It never touches any other line in the cart.
   - It does not publish PUB_SUB_EVENTS.cartUpdate. This theme's
     `CartItems.onCartUpdate()` always refetches section `main-cart-items`
     and would inject cart-PAGE markup into <cart-drawer-items>; the theme
     only gets away with it because its own writes publish with
     source === 'cart-items', which that subscriber ignores. Re-rendering the
     sections here (point 4) is complete on its own, so there is no reason to
     take that risk.
   - It leaves the product page, the bundle / quantity-break block, the
     sold-out fix, Shop Pay and the accelerated checkout buttons alone.
   ========================================================================== */

(function () {
  'use strict';

  var PROTECTION_QTY = 1; // one selected protection == one line, quantity 1.
  var ERROR_TEXT = 'We could not update your cart. Please try again.';

  var chain = Promise.resolve();
  var booted = false;

  /* ---------------------------------------------------------------- utils */

  function rootUrl() {
    var r = (window.routes && window.routes.root_url) || '/';
    return r === '/' ? '' : String(r).replace(/\/$/, '');
  }

  function url(path) {
    return rootUrl() + path;
  }

  function postJSON(path, payload) {
    return fetch(url(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      return response.json().then(function (json) {
        if (!response.ok || (json && json.status && json.status >= 400)) {
          var error = new Error((json && (json.description || json.message)) || 'Cart request failed');
          error.cartResponse = json;
          throw error;
        }
        return json;
      });
    });
  }

  function getCart() {
    return fetch(url('/cart.js'), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }).then(function (response) {
      if (!response.ok) throw new Error('Could not read the cart');
      return response.json();
    });
  }

  function cards() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-ngsp]'));
  }

  function variantIdOf(card) {
    return String(card.getAttribute('data-ngsp-variant') || '');
  }

  /* ------------------------------------------------------------ card view */

  function paint(card, checked) {
    var input = card.querySelector('[data-ngsp-input]');
    var shell = card.querySelector('[data-ngsp-card]');
    if (input && input.checked !== checked) input.checked = checked;
    if (shell) shell.classList.toggle('ngsp__card--on', checked);
  }

  function setBusy(state) {
    cards().forEach(function (card) {
      card.setAttribute('data-ngsp-busy', state ? 'true' : 'false');
      var input = card.querySelector('[data-ngsp-input]');
      if (input) input.setAttribute('aria-busy', state ? 'true' : 'false');
    });
  }

  function showError(message) {
    cards().forEach(function (card) {
      var slot = card.querySelector('[data-ngsp-error]');
      if (slot) slot.textContent = message || '';
    });
  }

  function syncCardsTo(cart) {
    var lines = (cart && cart.items) || [];
    cards().forEach(function (card) {
      var variantId = variantIdOf(card);
      var present = lines.some(function (line) {
        return String(line.variant_id) === variantId;
      });
      paint(card, present);
    });
  }

  /* -------------------------------------------------------- section render
     Mirrors assets/cart.js: every target is {id, section, selector}, the
     element replaced is `#id <selector>` falling back to `#id` itself, and
     the replacement is the same selector's innerHTML out of the returned
     section markup. Only the targets that exist on this page are requested,
     which keeps the call under Shopify's section limit. */

  function sectionTargets() {
    var targets = [];

    if (document.getElementById('cart-icon-bubble')) {
      targets.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    }

    if (document.querySelector('#CartDrawer .drawer__inner')) {
      targets.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' });
    }

    var items = document.getElementById('main-cart-items');
    if (items && items.dataset.id) {
      targets.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
    }

    var footer = document.getElementById('main-cart-footer');
    if (footer && footer.dataset.id) {
      targets.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
    }

    return targets;
  }

  function renderSections(sections, targets) {
    if (!sections) return;

    targets.forEach(function (target) {
      var markup = sections[target.section];
      if (typeof markup !== 'string') return;

      var host = document.getElementById(target.id);
      if (!host) return;

      var replacement;
      try {
        replacement = new DOMParser().parseFromString(markup, 'text/html').querySelector(target.selector);
      } catch (e) {
        return;
      }
      if (!replacement) return;

      var elementToReplace = host.querySelector(target.selector) || host;
      elementToReplace.innerHTML = replacement.innerHTML;
    });
  }

  function toggleEmptyState(itemCount) {
    if (typeof itemCount !== 'number') return;
    var empty = itemCount === 0;

    ['cart-drawer', 'cart-items', 'cart-drawer-items'].forEach(function (selector) {
      var el = document.querySelector(selector);
      if (el) el.classList.toggle('is-empty', empty);
    });

    var footer = document.getElementById('main-cart-footer');
    if (footer) footer.classList.toggle('is-empty', empty);
  }

  /* Re-rendering a section replaces the card's DOM node, so a keyboard user
     would lose focus. Only taken back if focus landed nowhere in the
     meantime, and never with a scroll jump. */
  function restoreFocus(id) {
    if (!id) return;
    var active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement) return;

    var input = document.getElementById(id);
    if (!input) return;
    try {
      input.focus({ preventScroll: true });
    } catch (e) {
      /* older browsers: a focus without options is still better than none */
      input.focus();
    }
  }

  /* ----------------------------------------------------------- reconcile */

  /* Brings the cart in line with `desired` for one variant id, in as few
     writes as possible, and never touches any other line. */
  function reconcile(variantId, desired) {
    var targets = sectionTargets();
    var sectionsUrl = window.location.pathname;
    var sectionList = targets.map(function (t) {
      return t.section;
    });

    return getCart().then(function (cart) {
      var lines = ((cart && cart.items) || []).filter(function (line) {
        return String(line.variant_id) === String(variantId);
      });

      // Nothing to do - the cart already says what the shopper asked for.
      if (desired && lines.length === 1 && lines[0].quantity === PROTECTION_QTY) return null;
      if (!desired && lines.length === 0) return null;

      var work = Promise.resolve(null);

      if (!desired) {
        // Remove every protection line, by line key, newest first so the
        // earlier keys stay valid while we walk the list.
        lines
          .slice()
          .reverse()
          .forEach(function (line) {
            work = work.then(function () {
              return postJSON('/cart/change.js', {
                id: line.key,
                quantity: 0,
                sections: sectionList,
                sections_url: sectionsUrl
              });
            });
          });
        return work;
      }

      if (lines.length === 0) {
        return work.then(function () {
          return postJSON('/cart/add.js', {
            items: [{ id: Number(variantId), quantity: PROTECTION_QTY }],
            sections: sectionList,
            sections_url: sectionsUrl
          });
        });
      }

      // Already in the cart: cap the first line at 1 and drop any extras.
      // Shopify merges identical lines, so extras are a belt-and-braces path.
      lines
        .slice(1)
        .reverse()
        .forEach(function (line) {
          work = work.then(function () {
            return postJSON('/cart/change.js', {
              id: line.key,
              quantity: 0,
              sections: sectionList,
              sections_url: sectionsUrl
            });
          });
        });

      if (lines[0].quantity !== PROTECTION_QTY) {
        work = work.then(function () {
          return postJSON('/cart/change.js', {
            id: lines[0].key,
            quantity: PROTECTION_QTY,
            sections: sectionList,
            sections_url: sectionsUrl
          });
        });
      }

      return work;
    }).then(function (result) {
      if (result) {
        renderSections(result.sections, targets);
        toggleEmptyState(result.item_count);
      }
      return result;
    });
  }

  /* -------------------------------------------------------------- toggle */

  function onToggle(input) {
    var card = input.closest('[data-ngsp]');
    if (!card) return;

    var variantId = variantIdOf(card);
    if (!variantId) return;

    var desired = input.checked;
    var focusId = document.activeElement === input ? input.id : null;

    // Optimistic visual state on every card, so the tap feels immediate.
    showError('');
    paint(card, desired);

    chain = chain
      .then(function () {
        setBusy(true);
        return reconcile(variantId, desired);
      })
      .then(function () {
        return getCart().then(syncCardsTo, function () {});
      })
      .catch(function () {
        // Put the checkbox back to whatever the cart actually says.
        return getCart()
          .then(syncCardsTo, function () {
            paint(card, !desired);
          })
          .then(function () {
            showError(ERROR_TEXT);
          });
      })
      .then(function () {
        setBusy(false);
        restoreFocus(focusId);
      });

    return chain;
  }

  /* ---------------------------------------------------------------- boot */

  function onChangeCapture(event) {
    var input = event.target;
    if (!input || typeof input.matches !== 'function') return;
    if (!input.matches('[data-ngsp-input]')) return;

    // Keep this event away from CartItems' own change listener.
    event.stopPropagation();

    onToggle(input);
  }

  function syncFromCartSoon() {
    if (!cards().length) return;
    getCart().then(syncCardsTo, function () {});
  }

  function boot() {
    if (booted) return;
    booted = true;

    document.addEventListener('change', onChangeCapture, true);

    // Let the existing add-on orphan guard settle first, then take the cart
    // as the single source of truth for every card on the page.
    setTimeout(syncFromCartSoon, 700);

    // A page restored from the back/forward cache can show a stale cart.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) syncFromCartSoon();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
